
import React, { useEffect, useRef, useState } from 'react';
import { TrendingUp, Shield } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useAiPropRefLink } from '@/hooks/useAiPropRefLink';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

export const AIPropPromoCard = () => {
  const [isHovering, setIsHovering] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [showSparkles, setShowSparkles] = useState(false);
  const { t } = useTranslation();
  const { refLink, isLoading } = useAiPropRefLink();
  const isMobile = useIsMobile();
  
  useEffect(() => {
    setShowSparkles(true);
    const timer = setTimeout(() => {
      setShowSparkles(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current || isMobile) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (centerY - y) / 40;
    const rotateY = (x - centerX) / 40;
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };
  
  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    setIsHovering(false);
    cardRef.current.style.transition = 'transform 0.5s ease';
    cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg)';
    setTimeout(() => {
      if (cardRef.current) {
        cardRef.current.style.transition = '';
      }
    }, 500);
  };
  
  const handleStartChallenge = () => {
    // Track the click for analytics
    try {
      // Log click for analytics purposes
      console.log('AI Prop challenge started with referral link:', refLink);
      
      // Navigate to the AI Prop referral link
      window.open(refLink, '_blank');
    } catch (error) {
      console.error('Error navigating to AI Prop challenge:', error);
      toast.error(t('error_opening_link'));
    }
  };
  
  return (
    <div
      ref={cardRef}
      className="ai-prop-card-bw relative overflow-hidden rounded-xl transition-all duration-300 group"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={handleMouseLeave}
      style={{
        transformStyle: 'preserve-3d',
        transition: 'transform 0.5s ease',
        backgroundImage: `url('https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//ai-generated-subtle-dark-gradient-background-with-floor-horizontal-ai-generative-free-photo.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
      data-tour="ai-prop"
    >
      <div className="ai-prop-card-inner-bw relative z-10 backdrop-blur-[2px] rounded-xl shadow-glow-bw card-shine-bw">
        <div className={`sparkles-container-bw absolute inset-0 z-0 pointer-events-none opacity-50 ${showSparkles ? 'active' : ''}`}>
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="sparkle-bw"
              style={{
                left: `${Math.random() * 95}%`,
                top: `${Math.random() * 90}%`,
                animationDelay: `${Math.random() * 1.5}s`
              }}
            />
          ))}
        </div>
        <CardHeader className="pb-2 md:pb-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="logo-container relative flex items-center justify-center">
              <div className="logo-bg-bw w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center relative z-10">
                <img
                  alt="AI Prop Logo"
                  className="w-6 h-6 md:w-9 md:h-9 object-contain z-20 logo-img-bw select-none"
                  draggable="false"
                  src="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//AI%20PROP%20logo%20ICON.png"
                />
              </div>
              {/* Enhanced purple logo glow */}
              <div className={`logo-glow-bw absolute inset-0 z-0 rounded-full ${showSparkles ? 'pulse-subtle-bw' : ''}`}></div>
            </div>
            <CardTitle className={`${isMobile ? "text-lg" : "text-2xl"} font-semibold bg-gradient-to-r from-white via-zinc-300 to-zinc-500 bg-clip-text text-transparent tracking-tight`}>
              {t('ai_prop_trading')}
            </CardTitle>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 md:space-y-6">
          <p className={`${isMobile ? "text-sm" : "text-lg"} text-gray-100 font-medium`}>
            {t('get_funded_description')}
          </p>
          <ul className="space-y-2 md:space-y-4">
            {[{
              icon: <TrendingUp className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-gray-600`} />,
              text: <>
                {t('seamless_integration')}
              </>
            }, {
              icon: <Shield className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-gray-600`} />,
              text: <>
                {t('higher_pass_rate')}
              </>
            }, {
              icon: <Shield className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-gray-600`} />,
              text: <>
                {t('risk_management')}
              </>
            }].map((benefit, index) => (
              <li key={index} className={`flex items-center gap-2 md:gap-3 text-gray-200 hover:text-white transition-transform duration-200 hover:translate-x-1 ${isMobile ? "text-xs" : "text-sm"}`}>
                <span>{benefit.icon}</span>
                {benefit.text}
              </li>
            ))}
          </ul>
          <p className={`${isMobile ? "text-[10px]" : "text-xs"} text-gray-400 border-t border-gray-700/60 pt-3 md:pt-4`}>
            {t('purchase_challenge')}
          </p>
        </CardContent>

        <CardFooter className="flex gap-2 md:gap-3 pt-0">
          <Button 
            size={isMobile ? "sm" : "lg"}
            className="flex-1 bg-white text-black font-semibold border border-black/20 hover:bg-gray-100 transition-all duration-200 relative overflow-hidden outline-none shadow-none rounded-lg px-3 md:px-6 py-2 md:py-3 mono-btn-bw"
            onClick={handleStartChallenge}
            disabled={isLoading}
          >
            <span className={`relative z-10 ${isMobile ? "text-xs" : ""}`}>{t('start_challenge')}</span>
            <div className="absolute inset-0 sparkle-trail-bw opacity-0 group-hover:opacity-100"></div>
          </Button>
          <Button 
            size={isMobile ? "sm" : "lg"} 
            variant="outline" 
            className={`flex-1 bg-black text-white border-black/80 hover:bg-black/90 transition-all duration-200 rounded-lg ${isMobile ? "text-xs" : ""}`}
          >
            {t('learn_more')}
          </Button>
        </CardFooter>
      </div>
    </div>
  );
};
